import { useCallback, useEffect, useState } from "react";

import { SelectData } from "../../../../../models";
import { searchOrganisationUsers, searchWorkflowTaskObjectGroups } from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { TasksDashboardFilterKey } from "../../../../../widget";
import { OBJECT_TYPE_LABELS, TASK_TYPE_LABELS } from "../../../../../widget/general/TasksDashboard/constants";
import { buildGroupsRequest } from "../utils";

/** Enough to cover an organisation's users and its objects that carry tasks without paging a dropdown. */
const OPTIONS_PAGE_SIZE = 50;

export interface UseTasksDashboardFilterOptionsArgs {
  /** Tag options, which come from the task connection's `aspects.tags` (BR-02825). */
  tagOptions: SelectData;
  refreshKey?: number;
}

export interface UseTasksDashboardFilterOptionsReturnData {
  filterOptions: Record<TasksDashboardFilterKey, SelectData>;
}

/**
 * The option lists behind the dashboard header's four filters. Each one is sourced so that its keys
 * are what the queries filter on — uuids, not display names — and so the list is the complete set
 * rather than whatever the loaded page of cards happens to mention (REQ-007):
 *
 *  - **Assignee** — the organisation's active users, with the signed-in user first.
 *  - **Task type** — static: a task either has a workflow step or it doesn't.
 *  - **Object** — the parent objects that have tasks, from the group query, so Projects, Deals and
 *    Landholdings all appear. Deliberately fetched *unfiltered*: an option list narrowed by the
 *    current selection would drop the very option the user is trying to change.
 *  - **Tag** — passed in from the task connection's aspects.
 */
export const useTasksDashboardFilterOptions = ({
  tagOptions,
  refreshKey = 0,
}: UseTasksDashboardFilterOptionsArgs): UseTasksDashboardFilterOptionsReturnData => {
  const { user, currentOrganisationUuid } = useAuth();

  const [assigneeOptions, setAssigneeOptions] = useState<SelectData>([]);
  const [objectOptions, setObjectOptions] = useState<SelectData>([]);

  /*
    Both loaders resolve to `undefined` on any failure — an error status, a missing payload, or a
    rejected request — rather than throwing, so a failed option list leaves the previous options in
    place instead of an unhandled rejection.
  */
  const loadAssignees = useCallback(async (organisationUuid: string): Promise<SelectData | undefined> => {
    try {
      const response = await searchOrganisationUsers({
        organisationUuid,
        paging: { beforeCursor: null, afterCursor: null, limit: OPTIONS_PAGE_SIZE },
        filter: { results: { active: { operator: "eq", value: true } } },
        sort: [{ key: "results.user.fullName", direction: "asc" }],
      });

      if (response.status !== Status.Success || !response.data) return undefined;

      return response.data.results.map((result) => ({
        key: result.user.uuid,
        value: result.user.fullName ?? `${result.user.firstName} ${result.user.lastName}`,
      }));
    } catch {
      return undefined;
    }
  }, []);

  const loadObjects = useCallback(async (): Promise<SelectData | undefined> => {
    try {
      const response = await searchWorkflowTaskObjectGroups(buildGroupsRequest({}, OPTIONS_PAGE_SIZE, null));

      if (response.status !== Status.Success || !response.data) return undefined;

      return response.data.results.map((group) => {
        const typeLabel = OBJECT_TYPE_LABELS[group.objectType.toLowerCase()] ?? group.objectType;

        return {
          key: group.objectUuid,
          // The type is part of the label so two objects sharing a name stay distinguishable.
          value: group.objectName ? `${group.objectName} · ${typeLabel}` : typeLabel,
        };
      });
    } catch {
      return undefined;
    }
  }, []);

  useEffect(() => {
    if (!currentOrganisationUuid) return undefined;

    // Tied to the effect's lifecycle, so a response for a previous organisation (or a reload that
    // has already been superseded) is discarded rather than applied.
    let ignore = false;

    loadAssignees(currentOrganisationUuid).then((options) => {
      if (ignore) return;

      if (options) setAssigneeOptions(options);
    });

    return () => {
      ignore = true;
    };
  }, [currentOrganisationUuid, loadAssignees, refreshKey]);

  useEffect(() => {
    let ignore = false;

    loadObjects().then((options) => {
      if (ignore) return;

      if (options) setObjectOptions(options);
    });

    return () => {
      ignore = true;
    };
  }, [loadObjects, refreshKey]);

  // The signed-in user sits at the top of the assignee list — the common case is "my tasks" (REQ-007).
  const currentUserUuid = user?.userUuid;
  const assignee = currentUserUuid
    ? [
        ...assigneeOptions.filter((option) => option.key === currentUserUuid),
        ...assigneeOptions.filter((option) => option.key !== currentUserUuid),
      ]
    : assigneeOptions;

  return {
    filterOptions: {
      assignee,
      taskType: Object.entries(TASK_TYPE_LABELS).map(([key, value]) => ({ key, value })),
      objectName: objectOptions,
      tag: tagOptions,
    },
  };
};
